// Client-side chord detection — the timeline behind the Studio's "Chords"
// lane. The heavy pass runs once per separation in a worker over the exact
// audio window the mixer plays, so segment times line up with the lanes 1:1.
// This module owns the model fetch/cache plumbing, the resample to the
// analysis rate, and the display formatting (transpose + key-aware spelling).
//
// Chord detection. Was beta-gated to a single account until 2026-08-18; now on
// for every signed-in user (see chordsBetaFor). Every entry point — analysis,
// render, persistence restore — still funnels through that one check.

import { cachedEngineBytes, putEngineBytes, readModelBody, isModelParseError, dropEngineBytes } from './modelCache';

export interface ChordSegment {
  start: number; // seconds, in mixer-window time
  end: number;
  label: string; // raw model label: "G:min" | "D#" | "N" (sharp spelling)
}

// Served from app/ (not vendor/): vendor/ is owned by the app's SFTP user on
// the server, so the master deploy account can't write there — app/ can.
export const CHORDS_MODEL_URL = '/app/chords-v1.onnx';
// Own cache name: the separation prefetch prunes rl-engine-v1 entries it
// doesn't know about, which would evict this model on every Studio visit.
const CHORDS_CACHE = 'rl-chords-v1';
const ANALYSIS_SR = 22050;

/**
 * GA since 2026-08-18 — chords are on for everyone. Kept as a function rather
 * than deleted because every entry point (analysis, render, persistence
 * restore) calls it, and one live switch is safer to reason about than a dozen
 * removed call sites. Return an allowlist check again to re-close it.
 *
 * ⚠️ This is NOT a plan gate. Free accounts still stop at
 * FREE_CHORDS_SECONDS (StudioNext), and locking the metronome to the song's
 * beats stays Pro-only — do not reintroduce `isPro || chordsEnabled`, which is
 * what made this flag double as a Pro bypass while it was owner-only.
 */
export function chordsBetaFor(_email: string | null | undefined): boolean {
  return true;
}

async function modelBytes(): Promise<Uint8Array> {
  const hit = await cachedEngineBytes(CHORDS_MODEL_URL, CHORDS_CACHE);
  if (hit) return hit;
  const res = await fetch(CHORDS_MODEL_URL);
  if (!res.ok) throw new Error(`chords model fetch failed: ${res.status}`);
  const bytes = await readModelBody(res, CHORDS_MODEL_URL);
  await putEngineBytes(CHORDS_MODEL_URL, bytes, CHORDS_CACHE);
  return bytes;
}

/** Downmix + resample to the analysis rate with the browser's resampler. */
async function toAnalysisMono(
  left: Float32Array,
  right: Float32Array,
  sampleRate: number,
): Promise<Float32Array> {
  const frames = Math.min(left.length, right.length);
  const outLen = Math.max(1, Math.ceil((frames * ANALYSIS_SR) / sampleRate));
  const oac = new OfflineAudioContext(1, outLen, ANALYSIS_SR);
  const buf = oac.createBuffer(2, frames, sampleRate);
  buf.copyToChannel(left.subarray(0, frames) as Float32Array<ArrayBuffer>, 0);
  buf.copyToChannel(right.subarray(0, frames) as Float32Array<ArrayBuffer>, 1);
  const src = oac.createBufferSource();
  src.buffer = buf;
  src.connect(oac.destination);
  src.start();
  const rendered = await oac.startRendering();
  return rendered.getChannelData(0);
}

/**
 * Detect the chord timeline for a stereo PCM window. Resolves to null when the
 * clip is too short to say anything. Never throws for analysis-quality reasons;
 * infrastructure failures (model fetch, worker) reject.
 */
export async function detectChords(
  left: Float32Array,
  right: Float32Array,
  sampleRate: number,
  onProgress?: (frac: number) => void,
): Promise<ChordSegment[] | null> {
  const duration = Math.min(left.length, right.length) / sampleRate;
  if (duration < 3) return null;

  const [mono, model] = await Promise.all([
    toAnalysisMono(left, right, sampleRate),
    modelBytes(),
  ]);
  // The worker wants a transferable ArrayBuffer that is exactly the bytes.
  const modelBuf =
    model.byteOffset === 0 && model.byteLength === model.buffer.byteLength
      ? (model.buffer as ArrayBuffer)
      : (model.slice().buffer as ArrayBuffer);

  return new Promise((resolve, reject) => {
    const w = new Worker(new URL('../workers/chordWorker.ts', import.meta.url), {
      type: 'module',
    });
    const bail = (err: unknown) => {
      w.terminate();
      reject(err instanceof Error ? err : new Error(String(err)));
    };
    w.onerror = (e) => bail(e.message || 'chord worker failed');
    w.onmessage = (e: MessageEvent) => {
      const m = e.data as
        | { type: 'progress'; done: number; total: number }
        | { type: 'done'; segments: ChordSegment[] }
        | { type: 'error'; message: string };
      if (m.type === 'progress') onProgress?.(m.done / m.total);
      else if (m.type === 'done') {
        w.terminate();
        resolve(m.segments);
      } else if (m.type === 'error') {
        // A cached model ORT can't parse fails every later run too: drop it
        // first, so the next analysis downloads a fresh copy.
        if (isModelParseError(m.message)) void dropEngineBytes(CHORDS_MODEL_URL, CHORDS_CACHE).then(() => bail(m.message));
        else bail(m.message);
      }
    };
    w.postMessage({ pcm: mono, model: modelBuf, duration }, [
      mono.buffer as ArrayBuffer,
      modelBuf,
    ]);
  });
}

// ── display formatting ────────────────────────────────────────────────────

const SHARP_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const FLAT_NAMES = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];
const NAME_TO_PC: Record<string, number> = {
  ...Object.fromEntries(SHARP_NAMES.map((n, i) => [n, i])),
  Db: 1, Eb: 3, Gb: 6, Ab: 8, Bb: 10, // accept flat spellings too
};
// Majors on the flat side of the circle of fifths: F, Bb, Eb, Ab, Db, Gb.
const FLAT_MAJOR_PCS = new Set([5, 10, 3, 8, 1, 6]);

/**
 * Whether chord roots should be spelled with flats, given the detected key
 * and the current transpose. G minor → Bb major territory → Eb, not D#.
 */
export function chordsPreferFlats(
  kb: { tonic: string; mode: 'major' | 'minor' } | null,
  semitones: number,
): boolean {
  if (!kb) return false;
  const pc = NAME_TO_PC[kb.tonic];
  if (pc == null) return false;
  const majorPc = (((kb.mode === 'minor' ? pc + 3 : pc) + semitones) % 12 + 12) % 12;
  return FLAT_MAJOR_PCS.has(majorPc);
}

/** "G:min" + transpose → "Am"/"Abm"…; null for the no-chord label. */
export function formatChord(
  raw: string,
  semitones: number,
  useFlats: boolean,
): string | null {
  if (raw === 'N' || raw === 'X') return null;
  const minor = raw.endsWith(':min');
  const root = minor ? raw.slice(0, -4) : raw;
  const pc = NAME_TO_PC[root];
  if (pc == null) return null;
  const t = ((pc + semitones) % 12 + 12) % 12;
  return (useFlats ? FLAT_NAMES : SHARP_NAMES)[t] + (minor ? 'm' : '');
}

/** The three pitch classes a triad label spells. */
function chordTones(raw: string): Set<number> | null {
  const c = parseChordLabel(raw);
  if (!c) return null;
  return new Set([c.pc, (c.pc + (c.minor ? 3 : 4)) % 12, (c.pc + 7) % 12]);
}

function parseChordLabel(raw: string): { pc: number; minor: boolean } | null {
  if (!raw || raw === 'N' || raw === 'X') return null;
  const minor = raw.endsWith(':min');
  const root = minor ? raw.slice(0, -4) : raw;
  const pc = NAME_TO_PC[root];
  return pc == null ? null : { pc, minor };
}

/**
 * Fold very short segments into the neighbour they share the most notes with.
 *
 * This is the single biggest source of "that chord is wrong" — not noise, but
 * UPPER-STRUCTURE confusion: the model hears the top three notes of a richer
 * chord as a triad of their own (the A-C-E inside Fmaj7 reads as Am, the E-G-B
 * inside Cmaj7 reads as Em). Both our maj/min head AND the large-vocabulary
 * head report these at >0.95 confidence, so no probability threshold or key
 * prior can catch them — but they are always brief and always share two notes
 * with the real chord, which is exactly what this tests. Measured against
 * Moises' own labels on the owner's reference song, this turns our 8-chord
 * timeline into their 7-chord one, boundaries within 0.4 s.
 *
 * A short segment whose neighbours share fewer than two notes is a real chord
 * change and is left alone.
 */
export function simplifyChords(
  segs: ChordSegment[],
  beatSec?: number,
  key?: { tonic: string; mode: 'major' | 'minor' } | null,
): ChordSegment[] {
  if (segs.length < 3) return segs;
  const playable = segs.filter((s) => chordTones(s.label));
  if (playable.length < 3) return segs;

  // Two thresholds, both required. ABSOLUTE (~1.3 beats) catches the blip;
  // RELATIVE (well under this song's own harmonic rhythm) is what stops a
  // genuine one-chord-per-beat progression from being eaten — there, every
  // chord is short, so nothing is anomalous and nothing merges.
  const spans = playable.map((s) => s.end - s.start).sort((a, b) => a - b);
  const median = spans[spans.length >> 1];
  const maxShort = Math.min(beatSec && beatSec > 0 ? beatSec * 1.3 : 1.0, median * 0.6);

  // Diatonic triads of the detected key — used only to decide DIRECTION.
  const diatonic = (() => {
    if (!key) return null;
    const pc = NAME_TO_PC[key.tonic];
    if (pc == null) return null;
    const root = key.mode === 'minor' ? pc : pc;
    const steps: [number, boolean][] = key.mode === 'minor'
      ? [[0, true], [3, false], [5, true], [7, false], [8, false], [10, false]] // i III iv V(harm) VI VII
      : [[0, false], [2, true], [4, true], [5, false], [7, false], [9, true]];  // I ii iii IV V vi
    const set = new Set<string>();
    for (const [iv, min] of steps) set.add(`${(root + iv) % 12}:${min}`);
    return set;
  })();
  const isDiatonic = (label: string): boolean => {
    if (!diatonic) return false;
    const c = parseChordLabel(label);
    return !!c && diatonic.has(`${c.pc}:${c.minor}`);
  };

  const out = segs.map((s) => ({ ...s }));
  for (let guard = 0; guard < segs.length; guard++) {
    let merged = false;
    // Most anomalous first, so the outcome never depends on scan order.
    const order = out
      .map((s, i) => ({ i, d: s.end - s.start }))
      .filter((x) => chordTones(out[x.i].label) && x.d < maxShort)
      .sort((a, b) => a.d - b.d);

    for (const { i } of order) {
      const seg = out[i];
      const tones = chordTones(seg.label)!;
      // A chord that belongs to the key is evidence, not an artifact: never
      // dissolve it into a neighbour that doesn't. Without this the winner
      // between two equally short segments (our reference song had Am 0.84 s
      // beside F 0.83 s) came down to array order.
      const segDiatonic = isDiatonic(seg.label);
      let best: { share: number; dia: number; span: number; j: number } | null = null;
      for (const j of [i - 1, i + 1]) {
        const nb = out[j];
        if (!nb) continue;
        const nbTones = chordTones(nb.label);
        if (!nbTones) continue;
        if (segDiatonic && !isDiatonic(nb.label)) continue;
        let share = 0;
        for (const t of tones) if (nbTones.has(t)) share++;
        const cand = { share, dia: isDiatonic(nb.label) ? 1 : 0, span: nb.end - nb.start, j };
        if (!best || cand.share > best.share
          || (cand.share === best.share && cand.dia > best.dia)
          || (cand.share === best.share && cand.dia === best.dia && cand.span > best.span)) best = cand;
      }
      if (!best || best.share < 2) continue;
      if (best.j < i) out[best.j].end = seg.end;
      else out[best.j].start = seg.start;
      out.splice(i, 1);
      merged = true;
      break;
    }
    if (!merged) break;
  }
  return out;
}

// ── key from chords ───────────────────────────────────────────────────────
// The whole-clip chromagram key detector (keyBpm.ts) hears everything —
// drums, melody, overtones — and drifts on real mixes. The chord timeline is
// already a denoised harmonic analysis, so when it exists we re-derive the
// key from it: duration-weighted chord tones scored against the
// Krumhansl–Kessler profiles, plus three cadence cues the raw audio can't
// give — tonic-triad presence, the V-major-in-minor (harmonic-minor leading
// tone) marker, and first/last-chord tonic bias.

const KK_MAJOR = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
const KK_MINOR = [6.33, 2.68, 3.52, 5.38, 2.6, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];

export interface ChordKeyEstimate {
  tonic: string; // sharp spelling — internal lookups expect it
  mode: 'major' | 'minor';
  keyShort: string; // display spelling: "Bbm", not "A#m"
  keyName: string; // "Bb minor"
  confidence: number; // 0..1, from the margin over the runner-up
}

function pearson12(a: ArrayLike<number>, b: ArrayLike<number>): number {
  let ma = 0, mb = 0;
  for (let i = 0; i < 12; i++) { ma += a[i]; mb += b[i]; }
  ma /= 12; mb /= 12;
  let num = 0, da = 0, db = 0;
  for (let i = 0; i < 12; i++) {
    const x = a[i] - ma, y = b[i] - mb;
    num += x * y; da += x * x; db += y * y;
  }
  const den = Math.sqrt(da * db);
  return den === 0 ? 0 : num / den;
}

/** Estimate the key from a detected chord timeline; null when there isn't
    enough harmonic material to call it (< 8 s of chords). */
export function keyFromChords(segs: ChordSegment[]): ChordKeyEstimate | null {
  const pcw = new Float64Array(12);
  const durByChord = new Map<string, number>(); // "<pc>:<m|M>" → seconds
  let total = 0;
  let first: { pc: number; minor: boolean } | null = null;
  let last: { pc: number; minor: boolean } | null = null;

  for (const s of segs) {
    if (s.label === 'N' || s.label === 'X') continue;
    const minor = s.label.endsWith(':min');
    const root = minor ? s.label.slice(0, -4) : s.label;
    const pc = NAME_TO_PC[root];
    const d = s.end - s.start;
    if (pc == null || d <= 0) continue;
    total += d;
    pcw[pc] += d; // root carries the chord
    pcw[(pc + (minor ? 3 : 4)) % 12] += d * 0.75;
    pcw[(pc + 7) % 12] += d * 0.75;
    const k = `${pc}:${minor ? 'm' : 'M'}`;
    durByChord.set(k, (durByChord.get(k) ?? 0) + d);
    const cur = { pc, minor };
    if (!first) first = cur;
    last = cur;
  }
  if (total < 8) return null;

  const frac = (pc: number, minor: boolean) =>
    (durByChord.get(`${pc}:${minor ? 'm' : 'M'}`) ?? 0) / total;

  let best = { score: -Infinity, t: 0, mode: 'major' as 'major' | 'minor' };
  let second = -Infinity;
  const rot = new Float64Array(12);
  for (let t = 0; t < 12; t++) {
    for (let i = 0; i < 12; i++) rot[i] = pcw[(t + i) % 12];
    for (const mode of ['major', 'minor'] as const) {
      const minor = mode === 'minor';
      let score = pearson12(rot, minor ? KK_MINOR : KK_MAJOR);
      // The key's own triad, with the right quality, actually being played.
      score += 0.35 * frac(t, minor);
      // A MAJOR triad on the fifth degree of a minor key is the raised
      // leading tone — the single strongest minor-vs-relative-major cue.
      if (minor) score += 0.2 * Math.min(1, frac((t + 7) % 12, false) * 3);
      // Songs tend to open and close on the tonic.
      if (first && first.pc === t && first.minor === minor) score += 0.05;
      if (last && last.pc === t && last.minor === minor) score += 0.08;
      if (score > best.score) { second = best.score; best = { score, t, mode }; }
      else if (score > second) second = score;
    }
  }

  const minor = best.mode === 'minor';
  const majorPc = minor ? (best.t + 3) % 12 : best.t;
  const names = FLAT_MAJOR_PCS.has(majorPc) ? FLAT_NAMES : SHARP_NAMES;
  return {
    tonic: SHARP_NAMES[best.t],
    mode: best.mode,
    keyShort: names[best.t] + (minor ? 'm' : ''),
    keyName: `${names[best.t]} ${best.mode}`,
    confidence: Math.max(0, Math.min(1, 0.3 + (best.score - second) * 4)),
  };
}
